/**
 * Canonical class recipes.
 *
 * Primitives and presets both build from here, so a preset emits byte-identical markup to the
 * equivalent hand-assembled section. That is what makes a preset a head start rather than a
 * parallel implementation that slowly drifts from the thing it is supposed to shortcut.
 */

import type { ClassRef } from '@lp/shared';
import { boxCls } from './util.js';

/** A full-bleed band. Vertical rhythm lives here; horizontal padding lives on the container. */
export function sectionCls(tone: string, padY = '9'): Array<string | ClassRef> {
  return boxCls(`is-pad-y-${padY}`, tone && `is-bg-${tone}`);
}

/**
 * The centred content column inside a section.
 *
 * NOTE: centring uses `is-text-center`, never `is-align-center`. On a flex column
 * `align-items: center` shrinks every child to its own content width — a heading stops
 * filling the column and a grid collapses to the width of its widest cell. That was a real
 * bug: the feature grid's heading and cards both mis-sized because of it.
 */
export function containerCls(
  opts: {
    width?: 'narrow' | 'normal' | 'wide' | 'full';
    gap?: string;
    align?: 'left' | 'center' | 'right';
    /** The page gutter — space at the left and right of the content column. */
    padX?: string;
  } = {},
): Array<string | ClassRef> {
  return boxCls(
    'is-flex',
    'is-col',
    'is-cell',
    `is-gap-${opts.gap ?? '6'}`,
    `is-w-${opts.width ?? 'normal'}`,
    // 20px by default, matching the live site's own gutter. Never zero: content touching
    // the screen edge on a phone is the most common way a generated page looks broken.
    `is-pad-x-${opts.padX ?? '5'}`,
    opts.align && opts.align !== 'left' && `is-text-${opts.align}`,
  );
}

export function stackCls(
  opts: {
    row?: boolean;
    /** Flips a row visually, leaving the DOM order (and so the reading order) alone. */
    reverse?: boolean;
    gap?: string;
    align?: 'start' | 'center' | 'end' | 'stretch';
    justify?: 'start' | 'center' | 'end' | 'between';
    textAlign?: 'left' | 'center' | 'right';
    wrap?: boolean;
    stackOnMobile?: boolean;
  } = {},
): Array<string | ClassRef> {
  return boxCls(
    'is-flex',
    opts.row ? (opts.reverse ? 'is-row-reverse' : 'is-row') : 'is-col',
    // A Box is a slot too, so it picks up Puck's `.DropZone { height: 100% }` and inherits the
    // same problem as a column: full height, content pinned to the top, and its own vertical
    // alignment doing nothing. See `is-cell`.
    'is-cell',
    opts.row && opts.stackOnMobile !== false && 'is-stack-sm',
    `is-gap-${opts.gap ?? '5'}`,
    opts.justify && `is-justify-${opts.justify}`,
    // Only meaningful on a row. On a column it shrinks children — see containerCls.
    opts.align && (opts.row || opts.align === 'stretch') && `is-align-${opts.align}`,
    opts.wrap && 'is-wrap',
    opts.textAlign && opts.textAlign !== 'left' && `is-text-${opts.textAlign}`,
  );
}

export function gridCls(
  columns: string | number,
  gap = '4',
  align: 'start' | 'center' | 'stretch' = 'stretch',
): Array<string | ClassRef> {
  // `is-cell` for the same reason as a Box: a Grid nested in a column is a slot, and a slot
  // that keeps Puck's `height: 100%` stretches instead of sitting at its natural height.
  return boxCls('is-grid', 'is-cell', `is-cols-${columns}`, `is-gap-${gap}`, `is-align-${align}`);
}

/**
 * A bordered card.
 *
 * `is-fill-height` matters inside a grid: without it a short card sits at its natural height
 * and the row looks ragged, because the grid stretches the *cell*, not the card inside it.
 */
export function cardCls(opts: { tone?: string; padding?: string } = {}): Array<string | ClassRef> {
  return boxCls(
    'is-flex',
    'is-col',
    'is-gap-3',
    `is-pad-${opts.padding ?? '5'}`,
    `is-bg-${opts.tone ?? 'white'}`,
    'is-radius-md',
    'is-border',
    'is-fill-height',
  );
}

/** A plain card with no border or background — the live site's 3-up icon cards. */
export function plainCardCls(): Array<string | ClassRef> {
  return boxCls('is-flex', 'is-col', 'is-gap-3', 'is-align-center', 'is-text-center');
}

/** The frosted purple panel from every /lp/ page's "how it works" band. */
export function panelCls(): Array<string | ClassRef> {
  return boxCls('is-flex', 'is-col', 'is-gap-5', 'is-pad-5', 'is-glass', 'is-radius-lg');
}

/**
 * A row of calls to action.
 *
 * Alignment has to live here rather than being inherited. `text-align: center` on the parent
 * centres *text*; it does nothing to a flex row, which keeps hugging the start of the column.
 * That is why the CTA band's heading and copy centred while its button sat on the left.
 */
export function ctaGroupCls(
  align: 'left' | 'center' | 'right' = 'left',
): Array<string | ClassRef> {
  const justify = align === 'center' ? 'center' : align === 'right' ? 'end' : 'start';
  return boxCls('is-flex', 'is-row', 'is-wrap', 'is-gap-3', 'is-align-center', `is-justify-${justify}`);
}
